// components/partner/PartnerRegistration.tsx
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Loader2,
  EyeIcon,
  EyeOffIcon,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import ApiService, { ApiError } from "@/services/api";
import { EndpointKeys } from "@/config/api";
import type { RegisterResponse } from "@/types/user";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export interface ApiErrorResponse {
  error?: string;
}

const formSchema = z
  .object({
    email: z.string().email("Invalid email address"),
    companyName: z
      .string()
      .min(2, "Company name must be at least 2 characters"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof formSchema>;

const PartnerRegistration: React.FC = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [showPassword, setShowPassword] = React.useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      companyName: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      setError(undefined);

      const response = await ApiService.request<RegisterResponse>(
        EndpointKeys.SIGNUP,
        {
          method: "POST",
          body: {
            email: data.email,
            password1: data.password,
            password2: data.confirmPassword,
            company_name: data.companyName,
            user_type: "PARTNER",
          },
          skipAuth: true,
        },
        false,
      );

      toast({
        title: "Success",
        description: "Partner account created successfully! Please log in.",
      });

      await new Promise((r) => setTimeout(r, 2000));

      // Redirect to partner login
      window.location.href = "/login";
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.body.error
          : "Failed to create partner account";
      setError(message);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full bg-background min-h-screen">
      {/* Hero Section with Secondary background */}
      <header className="bg-secondary text-secondary-foreground py-12 px-4 text-center mb-10">
        <h1 className="text-4xl font-extrabold tracking-tight mb-3">
          PARTNER WITH NANNY PAY PRO
        </h1>
        <p className="text-lg max-w-2xl mx-auto">
          Become a partner and earn cash for every referral. Let's create
          supportive childcare communities together.
        </p>
      </header>

      <div className="max-w-md mx-auto p-6">
        <Card className="border-2 border-secondary shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-primary">
              Get started with your partner account
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="text-muted-foreground mb-6 text-center text-sm">
              <p className="mb-2">
                Receive cash credit into your bank account for each referral.
              </p>
              <p className="font-semibold">
                Starting at £100 to a maximum of £600 per paid client sign up.
              </p>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-5"
              >
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Your Company Name"
                          disabled={isLoading}
                          className="border-primary/20 focus:border-secondary"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="you@company.com"
                          disabled={isLoading}
                          className="border-primary/20 focus:border-secondary"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={showPassword ? "text" : "password"}
                            disabled={isLoading}
                            className="border-primary/20 focus:border-secondary"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 text-primary"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOffIcon className="h-4 w-4" />
                            ) : (
                              <EyeIcon className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                      <FormDescription className="text-xs">
                        <ul className="list-disc list-inside space-y-1">
                          <li>One lowercase character</li>
                          <li>One uppercase character</li>
                          <li>One number</li>
                          <li>8 characters minimum</li>
                        </ul>
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={showPassword ? "text" : "password"}
                            disabled={isLoading}
                            className="border-primary/20 focus:border-secondary"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 mt-4"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Get Started! <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </Form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-secondary hover:underline font-medium"
              >
                Log in
              </Link>
            </div>

            <div className="mt-8 text-xs text-center text-muted-foreground">
              By clicking the "Get Started!" button, you agree to our{" "}
              <a
                href="https://www.hirelo.io/legal-bits"
                className="text-secondary hover:underline"
              >
                Terms of Use
              </a>{" "}
              and{" "}
              <a
                href="https://www.hirelo.io/legal-bits"
                className="text-secondary hover:underline"
              >
                Privacy Policy
              </a>
              .
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center p-6 bg-accent rounded-lg">
          <p className="text-muted-foreground text-sm">
            We create supportive childcare communities by helping parents and
            nannies navigate HMRC compliance. Our modern platform allows parents
            and nannies to automate all aspects of payroll, saving time and
            pain.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerRegistration;
